import { useState } from 'react';

import type {
  CreateRecurringBillTemplate,
  CreateRecurringBillTemplateLine,
  RecurringBillTemplateWithLines,
  RecurringFrequency,
  UpdateRecurringBillTemplate,
} from '@dcc-erp/shared';
import { toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useVendors } from '../../lib/queries';
import { usePaymentTerms } from '../../lib/queries/payment-terms';
import {
  useCreateRecurringTemplate,
  useUpdateRecurringTemplate,
} from '../../lib/queries/recurring-bills';
import { AppModal } from '../AppModal';
import { modalError, modalLabel } from '../ar/modal-style';
import { BranchSelect, DepartmentSelect } from '../dimensions';
import { SearchSelect } from '../SearchSelect';

// =============================================================================
// RecurringTemplateFormModal — create / edit a recurring template.
// =============================================================================

export interface RecurringTemplateFormModalProps {
  companyId: string;
  mode: 'add' | 'edit';
  initial?: RecurringBillTemplateWithLines;
  onClose: () => void;
}

export function RecurringTemplateFormModal({
  companyId,
  mode,
  initial,
  onClose,
}: RecurringTemplateFormModalProps) {
  const { data: vendors } = useVendors(companyId, true);
  const { data: paymentTerms = [] } = usePaymentTerms(companyId, true);

  const create = useCreateRecurringTemplate(companyId);
  const update = useUpdateRecurringTemplate(companyId);

  // Header state
  const [vendorId, setVendorId] = useState(initial?.vendorId ?? '');
  const [branchId, setBranchId] = useState(initial?.branchId ?? '');
  const [departmentId, setDepartmentId] = useState(initial?.departmentId ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [vatRate, setVatRate] = useState(String(toNumber(initial?.vatRate ?? 7)));
  const [frequency, setFrequency] = useState<RecurringFrequency>(initial?.frequency ?? 'MONTHLY');
  const [dayOfMonth, setDayOfMonth] = useState(String(initial?.dayOfMonth ?? 1));
  const [startDate, setStartDate] = useState(
    initial?.startDate ? String(initial.startDate).slice(0, 10) : '',
  );
  const [endDate, setEndDate] = useState(
    initial?.endDate ? String(initial.endDate).slice(0, 10) : '',
  );
  const [paymentTermCode, setPaymentTermCode] = useState(initial?.paymentTermCode ?? '');

  // Lines state
  type LineState = CreateRecurringBillTemplateLine & { _key: number };
  const [lines, setLines] = useState<LineState[]>(
    initial?.lines.length
      ? initial.lines.map((l, i) => ({
          _key: i,
          description: l.description,
          qty: toNumber(l.qty),
          unitPrice: toNumber(l.unitPrice),
          expenseCode: l.expenseCode,
        }))
      : [{ _key: 0, description: '', qty: 1, unitPrice: 0, expenseCode: '' }],
  );
  const [nextKey, setNextKey] = useState(initial?.lines.length ?? 1);

  const [formError, setFormError] = useState<string | null>(null);
  const submitting = create.isPending || update.isPending;

  function updateLine(key: number, patch: Partial<LineState>) {
    setLines((prev) => prev.map((l) => (l._key === key ? { ...l, ...patch } : l)));
  }

  function addLine() {
    setLines((prev) => [
      ...prev,
      { _key: nextKey, description: '', qty: 1, unitPrice: 0, expenseCode: '' },
    ]);
    setNextKey((k) => k + 1);
  }

  function removeLine(key: number) {
    setLines((prev) => prev.filter((l) => l._key !== key));
  }

  const validLines = lines.filter(
    (l) => l.description.trim() && Number(l.qty) > 0 && Number(l.unitPrice) >= 0 && l.expenseCode,
  );

  const disabled =
    !vendorId ||
    !description.trim() ||
    !startDate ||
    !Number.isFinite(Number(dayOfMonth)) ||
    Number(dayOfMonth) < 1 ||
    Number(dayOfMonth) > 28 ||
    validLines.length === 0 ||
    submitting;

  async function handleSubmit() {
    setFormError(null);
    const cleanLines = validLines.map((l) => ({
      description: l.description.trim(),
      qty: Number(l.qty),
      unitPrice: Number(l.unitPrice),
      expenseCode: l.expenseCode.trim(),
    }));

    try {
      if (mode === 'edit' && initial) {
        const data: UpdateRecurringBillTemplate = {
          description: description.trim(),
          vatRate: Number(vatRate),
          frequency,
          dayOfMonth: Number(dayOfMonth),
          ...(branchId ? { branchId } : { branchId: null }),
          ...(departmentId ? { departmentId } : { departmentId: null }),
          ...(endDate ? { endDate } : { endDate: null }),
          paymentTermCode: paymentTermCode || null,
          lines: cleanLines,
        };
        await update.mutateAsync({ id: initial.id, data });
      } else {
        const data: CreateRecurringBillTemplate = {
          vendorId,
          branchId: branchId || undefined,
          departmentId: departmentId || undefined,
          description: description.trim(),
          vatRate: Number(vatRate),
          frequency,
          dayOfMonth: Number(dayOfMonth),
          startDate,
          endDate: endDate || undefined,
          paymentTermCode: paymentTermCode || undefined,
          lines: cleanLines,
        };
        await create.mutateAsync(data);
      }
      onClose();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  // WS8 R2 — standard AppModal (1200px) replaces the bespoke 820px chrome;
  // the submit/cancel buttons move to AppModal's footer slot.
  return (
    <AppModal
      title={mode === 'edit' ? 'แก้ไข Template บิลประจำ' : 'เพิ่ม Template บิลประจำ'}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </>
      }
    >
      {/* Row 1: Vendor + Branch + Dept */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>ผู้ขาย</label>
          <select
            className="select"
            value={vendorId}
            onChange={(e) => setVendorId(e.target.value)}
            disabled={mode === 'edit'}
            style={{ width: '100%', height: 38, fontSize: 14 }}
            aria-label="ผู้ขาย"
          >
            <option value="">— เลือกผู้ขาย —</option>
            {vendors?.map((v) => (
              <option key={v.id} value={v.id}>
                {v.code} · {v.nameTh}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={modalLabel}>สาขา</label>
          <BranchSelect
            companyId={companyId}
            value={branchId ?? ''}
            onChange={setBranchId}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>หน่วยงาน</label>
          <DepartmentSelect
            companyId={companyId}
            value={departmentId ?? ''}
            onChange={setDepartmentId}
            branchId={branchId ?? ''}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
      </div>

      {/* Row 2: Description */}
      <div>
        <label style={modalLabel}>รายละเอียด</label>
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="เช่น ค่าเช่าพื้นที่ สาขา XYZ"
          style={{ width: '100%', height: 38, fontSize: 14 }}
          aria-label="รายละเอียด template"
        />
      </div>

      {/* Row 3: Frequency + DayOfMonth + VAT + PaymentTerm */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 100px 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>ความถี่</label>
          <select
            className="select"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as RecurringFrequency)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
            aria-label="ความถี่"
          >
            <option value="MONTHLY">รายเดือน</option>
            <option value="QUARTERLY">รายไตรมาส</option>
            <option value="YEARLY">รายปี</option>
          </select>
        </div>
        <div>
          <label style={modalLabel}>วันที่ (1–28)</label>
          <input
            type="number"
            className="input tnum"
            value={dayOfMonth}
            min={1}
            max={28}
            step={1}
            onChange={(e) => setDayOfMonth(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
            aria-label="วันที่ตัดรอบ"
          />
        </div>
        <div>
          <label style={modalLabel}>VAT (%)</label>
          <input
            type="number"
            className="input tnum"
            value={vatRate}
            min={0}
            max={100}
            step={0.01}
            onChange={(e) => setVatRate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
            aria-label="VAT %"
          />
        </div>
        <div>
          <label style={modalLabel}>เงื่อนไขชำระ</label>
          <SearchSelect
            ariaLabel="เงื่อนไขชำระ template"
            value={paymentTermCode}
            onChange={setPaymentTermCode}
            options={[
              { value: '', label: '— ไม่ระบุ —' },
              ...paymentTerms.map((t) => ({ value: t.code, label: `${t.code} · ${t.label}` })),
            ]}
            getLabel={(v) =>
              v === ''
                ? '— ไม่ระบุ —'
                : (() => {
                    const t = paymentTerms.find((x) => x.code === v);
                    return t ? `${t.code} · ${t.label}` : v;
                  })()
            }
            placeholder="เลือกเงื่อนไขชำระ"
          />
        </div>
      </div>

      {/* Row 4: StartDate + EndDate */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>วันที่เริ่ม</label>
          <input
            type="date"
            className="input"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={mode === 'edit'}
            style={{ width: '100%', height: 38, fontSize: 14 }}
            aria-label="วันที่เริ่ม"
          />
        </div>
        <div>
          <label style={modalLabel}>วันที่สิ้นสุด (optional)</label>
          <input
            type="date"
            className="input"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
            aria-label="วันที่สิ้นสุด"
          />
        </div>
      </div>

      {/* Lines */}
      <div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 8,
            color: 'var(--text-muted)',
          }}
        >
          รายการค่าใช้จ่าย
        </div>
        <table className="tbl" style={{ tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th>คำอธิบาย</th>
              <th className="num" style={{ width: 80 }}>
                จำนวน
              </th>
              <th className="num" style={{ width: 110 }}>
                ราคา/หน่วย
              </th>
              <th style={{ width: 170 }}>รหัสบัญชีค่าใช้จ่าย</th>
              <th style={{ width: 30 }} />
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l._key}>
                <td>
                  <input
                    className="input"
                    value={l.description}
                    onChange={(e) => updateLine(l._key, { description: e.target.value })}
                    style={{ width: '100%', height: 32, fontSize: 13 }}
                    aria-label="คำอธิบาย line"
                  />
                </td>
                <td className="num">
                  <input
                    type="number"
                    className="input tnum"
                    value={l.qty}
                    min={0}
                    step={0.0001}
                    onChange={(e) => updateLine(l._key, { qty: Number(e.target.value) })}
                    style={{
                      width: '100%',
                      height: 32,
                      fontSize: 13,
                      textAlign: 'right',
                    }}
                    aria-label="จำนวน"
                  />
                </td>
                <td className="num">
                  <input
                    type="number"
                    className="input tnum"
                    value={l.unitPrice}
                    min={0}
                    step={0.01}
                    onChange={(e) => updateLine(l._key, { unitPrice: Number(e.target.value) })}
                    style={{
                      width: '100%',
                      height: 32,
                      fontSize: 13,
                      textAlign: 'right',
                    }}
                    aria-label="ราคาต่อหน่วย"
                  />
                </td>
                <td>
                  <input
                    className="input code"
                    value={l.expenseCode}
                    onChange={(e) => updateLine(l._key, { expenseCode: e.target.value })}
                    placeholder="เช่น 51010001"
                    style={{ width: '100%', height: 32, fontSize: 13 }}
                    aria-label="รหัสบัญชีค่าใช้จ่าย"
                  />
                </td>
                <td>
                  {lines.length > 1 && (
                    <button
                      className="btn ghost"
                      onClick={() => removeLine(l._key)}
                      style={{ padding: '0 6px' }}
                    >
                      <Icon name="x" size={14} />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button className="btn ghost" onClick={addLine} style={{ marginTop: 8, fontSize: 13 }}>
          <Icon name="plus" size={12} /> เพิ่มรายการ
        </button>
      </div>

      {formError && <div style={modalError}>บันทึกไม่สำเร็จ: {formError}</div>}
    </AppModal>
  );
}
